import Bugsnag from '@bugsnag/js';
import { Dispatch, SetStateAction } from 'react';
import DefaultLayout from 'components/layouts/DefaultLayout';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useRouter } from 'next/router';
import {
  useGetUserPreferenceQuery,
  useUpdateNotificationPreferencesMutation,
} from 'components/subscriptionPreferences/hooks';
import { getAsString } from 'helpers';
import ToggleSwitch from 'components/ui/ToggleSwitch/ToggleSwitch';
import styled from 'styled-components';
import Head from 'next/head';
import getConfig from 'next/config';
import boatsetterPaths from 'utils/boatsetterPaths';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import AccountSettingsLayout from 'components/layouts/AccountSettingsLayout';
import Holder from 'components/account_settings/Holder';

const LockIcon = (
  <svg
    style={{ margin: 'auto' }}
    width="10"
    height="14"
    viewBox="0 0 10 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.32204 5.34557V3.63882C8.32204 1.91247 6.91611 0.5 5.18321 0.5H4.81701C3.09065 0.5 1.67818 1.90593 1.67818 3.63882V5.34557C0.762686 5.4829 0.0629883 6.2676 0.0629883 7.22233V11.5971C0.0629883 12.6434 0.919628 13.5 1.96591 13.5H8.03431C9.08059 13.5 9.93723 12.6434 9.93723 11.5971V7.22233C9.93723 6.2676 9.23099 5.4829 8.32204 5.34557ZM8.95635 11.5971C8.95635 12.1071 8.54437 12.5191 8.03431 12.5191H1.96591C1.45585 12.5191 1.04387 12.1071 1.04387 11.5971V7.22233C1.04387 6.71227 1.45585 6.3003 1.96591 6.3003H8.03431C8.54437 6.3003 8.95635 6.71227 8.95635 7.22233V11.5971ZM7.34116 3.63882V5.31941H2.6656V3.63882C2.6656 2.44868 3.63341 1.48089 4.82355 1.48089H5.18975C6.37335 1.48089 7.34116 2.44868 7.34116 3.63882ZM5.92868 9.40643C5.92868 9.91649 5.51017 10.335 5.00011 10.335C4.49005 10.335 4.07154 9.91649 4.07154 9.40643C4.07154 8.89637 4.49005 8.47787 5.00011 8.47787C5.51017 8.47787 5.92868 8.89637 5.92868 9.40643Z"
      fill="#0751C2"
    />
  </svg>
);

const options = {
  email: {
    booking_activity_email: {
      label: 'Booking activity',
      disabled: true,
    },
    message_alerts_email: {
      label: 'Message alerts',
      disabled: false,
    },
    news_and_promotions_email: {
      label: 'News and promotions',
      disabled: false,
    },
    boat_management_activity_email: {
      label: 'Guest interactions on managed boats',
      disabled: false,
    },
  },
  sms: {
    booking_activity_sms: {
      label: 'Booking activity',
      disabled: false,
    },
    message_alerts_sms: {
      label: 'Message alerts',
      disabled: false,
    },
    news_and_promotions_sms: {
      label: 'News and promotions',
      disabled: false,
    },
  },
};

interface Props {
  section: string;
  state: any;
  updateState: Dispatch<SetStateAction<any>>;
  locked?: boolean;
  updating: boolean;
}

const OptionsComponent = ({ section, state, updateState, locked = false, updating }: Props) => (
  <div className="options">
    {Object.entries(options[section]).map(([key, settings]: [string, any]) => {
      const has_boats = state?.user?.boats_count > 0 || false;
      const all_boats_managed = state?.user?.are_all_owned_boats_managed || false;
      const isBoatManagementOption = key === 'boat_management_activity_email';

      if (isBoatManagementOption && !(has_boats && all_boats_managed)) {
        return null;
      }

      return (
        <div key={key} className="option">
          <div className="title">{settings.label}</div>
          {isBoatManagementOption ? (
            <Tooltip
              className="price_adjustment_tooltip"
              place="top"
              interactive
              trigger="mouseenter"
              triggerWith={
                <QuestionWrapper>
                  <QuestionMark />
                </QuestionWrapper>
              }
            >
              Guest interactions include messages and booking updates. You will still receive payout updates.
            </Tooltip>
          ) : null}
          <div className="switch">
            <ToggleSwitch
              round
              onLabel={null}
              isActive={state[key]}
              disabled={locked || updating || settings.disabled}
              onChange={() => {
                const newState = {
                  ...state,
                  [key]: !state[key],
                };
                updateState(newState);
              }}
              invertColor
              offLabel={null}
              icon={locked || settings.disabled ? LockIcon : null}
            />
          </div>
        </div>
      );
    })}
  </div>
);

const QuestionWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: 10px;
`;

const QuestionMark = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.5961 11.5961C9.05773 14.1345 4.94218 14.1345 2.40379 11.5961C-0.134598 9.05773 -0.134598 4.94218 2.40379 2.40379C4.94218 -0.134598 9.05773 -0.134597 11.5961 2.40379C14.1345 4.94218 14.1345 9.05773 11.5961 11.5961ZM10.8704 10.8704C8.73282 13.008 5.2671 13.008 3.1295 10.8704C0.991911 8.73282 0.991911 5.26709 3.1295 3.1295C5.2671 0.991911 8.73282 0.991911 10.8704 3.1295C13.008 5.2671 13.008 8.73282 10.8704 10.8704Z"
      fill="#0751c2"
    />
    <path
      d="M7.39463 8.4453H6.53205V8.00483C6.53205 7.84577 6.54734 7.70201 6.57793 7.57354C6.60852 7.44507 6.65134 7.32884 6.7064 7.22484C6.76758 7.12084 6.83793 7.02295 6.91746 6.93119C7.0031 6.83331 7.09793 6.73543 7.20193 6.63755L7.54145 6.31637C7.67604 6.20014 7.78616 6.07473 7.8718 5.94014C7.96357 5.79943 8.00945 5.62202 8.00945 5.40791C8.00945 5.11426 7.91463 4.88179 7.72498 4.7105C7.54145 4.53309 7.30287 4.44438 7.00922 4.44438C6.70334 4.44438 6.45864 4.54227 6.27511 4.73803C6.09158 4.93379 5.98452 5.17238 5.95393 5.45379L5.073 5.35285C5.1097 5.08979 5.18617 4.85426 5.30241 4.64626C5.41864 4.43215 5.56241 4.25168 5.7337 4.10486C5.91111 3.95803 6.11299 3.84792 6.33934 3.77451C6.56569 3.69498 6.80734 3.65521 7.06428 3.65521C7.32122 3.65521 7.5598 3.69192 7.78004 3.76533C8.00639 3.83874 8.20521 3.94886 8.37651 4.09568C8.5478 4.23639 8.68239 4.4138 8.78027 4.62791C8.87815 4.83591 8.92709 5.07756 8.92709 5.35285C8.92709 5.54861 8.90568 5.7199 8.86285 5.86673C8.82003 6.01355 8.75886 6.14814 8.67933 6.27049C8.60592 6.38672 8.51415 6.49684 8.40403 6.60084C8.29392 6.70484 8.17462 6.8119 8.04615 6.92201C7.9238 7.02601 7.8198 7.12084 7.73416 7.20648C7.64851 7.28601 7.58122 7.3686 7.53228 7.45425C7.48334 7.53989 7.44663 7.63472 7.42216 7.73871C7.40381 7.84271 7.39463 7.97118 7.39463 8.12412V8.4453ZM7.62404 9.73917C7.62404 9.89823 7.56592 10.0389 7.44969 10.1613C7.33345 10.2836 7.18663 10.3448 7.00922 10.3448C6.83793 10.3448 6.6911 10.2867 6.56875 10.1705C6.45252 10.0481 6.3944 9.90741 6.3944 9.74835C6.3944 9.58317 6.45252 9.44247 6.56875 9.32623C6.68499 9.20388 6.83181 9.14271 7.00922 9.14271C7.18663 9.14271 7.33345 9.20082 7.44969 9.31706C7.56592 9.43329 7.62404 9.574 7.62404 9.73917Z"
      fill="#0751c2"
    />
  </svg>
);

const NotificationsPage = () => {
  const router = useRouter();
  const { RAILS_URL } = getConfig().publicRuntimeConfig;

  const token = getAsString(router.query.token);

  const { notificationPreferences, isLoading, isIdle } = useGetUserPreferenceQuery(token, {
    onError: (err) => {
      if (err.response.data?.error_type === 'OAuthError') {
        router.push(
          `${boatsetterPaths.logIn}?redirect_to=${encodeURIComponent(
            window.location.pathname + window.location.search
          )}`
        );
      }
    },
  });

  const updateNotificationPreferencesMutation = useUpdateNotificationPreferencesMutation();

  const updateState = async (state) => {
    try {
      const queryParams: any = {};

      if (router.query.campaignId) queryParams.campaign_id = router.query.campaignId;
      if (router.query.templateId) queryParams.template_id = router.query.templateId;

      await updateNotificationPreferencesMutation.mutateAsync({
        body: {
          booking_activity_email: state.booking_activity_email,
          booking_activity_sms: state.booking_activity_sms,
          message_alerts_email: state.message_alerts_email,
          message_alerts_sms: state.message_alerts_sms,
          news_and_promotions_email: state.news_and_promotions_email,
          news_and_promotions_sms: state.news_and_promotions_sms,
          boat_management_activity_email: state.boat_management_activity_email,
          ...queryParams,
        },
        token,
      });
    } catch (e) {
      Bugsnag.notify(e);
      console.error(e);
    }
  };

  if (isLoading || isIdle || !notificationPreferences) return <DefaultLayout loading />;

  return (
    <DefaultLayout loading={isLoading} white>
      <Head>
        <link rel="canonical" href={`${RAILS_URL}${router.asPath}`} key="canonical" />
      </Head>
      <AccountSettingsLayout activeNav="notifications">
        <h2>Notification settings</h2>
        <Holder>
          <h3>Email</h3>
          <div>
            <p>
              Email preferences for: <b className="break">{notificationPreferences.user.email}</b>
            </p>
            <p>
              You cannot opt-out of emails related to your booking activity, but you can choose to opt out of SMS and
              PUSH.
            </p>
          </div>
          <OptionsComponent
            updateState={updateState}
            section="email"
            state={notificationPreferences}
            updating={updateNotificationPreferencesMutation.isLoading}
          />
        </Holder>
        <Holder>
          <h3>SMS</h3>
          <div>
            {notificationPreferences.sms_enabled ? (
              <>
                <p>
                  Phone number for SMS:{' '}
                  <b className="break">
                    {notificationPreferences?.user?.phone &&
                      parsePhoneNumber(notificationPreferences.user.phone).formatInternational()}
                  </b>
                </p>
                <p>
                  By checking an SMS box, you agree to receive texts from Boatsetter via an automated messaging system
                  to the telephone phone number listed above. You also acknowledge that your consent here represents
                  your electronic signature. Consent is not a condition to purchase any services. Reply HELP for help or
                  STOP to cancel. View our{' '}
                  <a href="https://www.boatsetter.com/legal/terms_of_service" target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>
                  ,{' '}
                  <a href="https://www.boatsetter.com/legal/privacy_policy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="/legal/terms_of_service#sms_terms_of_service" target="_blank" rel="noreferrer">
                    SMS Texting Policy
                  </a>
                  . Message frequency varies. Message and data rates may apply.
                </p>
              </>
            ) : (
              <p>
                Looks like we don&apos;t have a phone number for your account. To make changes, email{' '}
                <a className="supportEmail" href="mailto:contact@boatsetter.com">
                  contact@boatsetter.com
                </a>
              </p>
            )}
          </div>
          <OptionsComponent
            updateState={updateState}
            section="sms"
            state={notificationPreferences}
            locked={!notificationPreferences.sms_enabled}
            updating={updateNotificationPreferencesMutation.isLoading}
          />
        </Holder>
        <Holder>
          <h3>Push</h3>
          <div>
            <p>To manage your push notification settings, use the Boatsetter app.</p>
          </div>
        </Holder>
      </AccountSettingsLayout>
    </DefaultLayout>
  );
};

export default NotificationsPage;
